import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2, Download } from 'lucide-react';
import { statusLabel, levelLabel, levelBadgeClass, restrictionLabel, fmtDate, noticeLine, isNewChannelImpactCase, channelDisplayLabel, impactDisplayTags } from './conductUtils';

const ACTION_STATUS_LABELS = { PENDING: 'Pending', MET: 'Met', NOT_MET: 'Not met' };

async function downloadFile(fileId, filename) {
  try {
    const res = await api.get(`/conduct/files/${fileId}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (e) {
    toast.error('Failed to download file');
  }
}

export default function MyConductTab() {
  const [cases, setCases] = useState([]);
  const [cards, setCards] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [attached, setAttached] = useState({});
  const [uploading, setUploading] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [casesRes, cardsRes, restrRes, catRes] = await Promise.all([
        api.get('/conduct/me/cases'),
        api.get('/conduct/me/cards'),
        api.get('/conduct/me/restrictions'),
        api.get('/conduct/categories'),
      ]);
      setCases(casesRes.data);
      setCards(cardsRes.data);
      setRestrictions(restrRes.data.restriction_codes || []);
      setCategories(catRes.data);
    } catch (e) {
      toast.error('Failed to load your conduct records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categoryName = (id) => categories.find((c) => c.id === id)?.name || id;

  const submitStatement = async (caseId) => {
    const text = drafts[caseId] || '';
    if (!text.trim()) { toast.error('Please write a statement first'); return; }
    try {
      await api.post(`/conduct/me/cases/${caseId}/statement`, { text });
      toast.success('Statement submitted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to submit statement'); }
  };

  const acknowledge = async (cardId) => {
    try {
      await api.post(`/conduct/me/cards/${cardId}/acknowledge`);
      toast.success('Acknowledged');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const acceptCap = async (capId) => {
    try {
      await api.post(`/conduct/me/caps/${capId}/accept`);
      toast.success('CAP accepted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const fileAppeal = async (cardId) => {
    const grounds = drafts[`appeal_${cardId}`] || '';
    if (!grounds.trim()) { toast.error('Please state your grounds for appeal'); return; }
    try {
      await api.post(`/conduct/me/cards/${cardId}/appeal`, { grounds });
      toast.success('Appeal filed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to file appeal'); }
  };

  const showCauseReply = async (cardId) => {
    const reply = drafts[`sc_${cardId}`] || '';
    if (!reply.trim()) { toast.error('Please write your reply first'); return; }
    try {
      await api.post(`/conduct/me/cards/${cardId}/show-cause-reply`, { reply });
      toast.success('Reply submitted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const uploadFile = async (kind, id, file) => {
    const key = `${kind}_${id}`;
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const form = new FormData();
      form.append('file', file);
      const path = kind === 'case' ? `/conduct/me/cases/${id}/files` : `/conduct/me/cards/${id}/files`;
      const res = await api.post(path, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttached((a) => ({ ...a, [key]: [...(a[key] || []), { id: res.data.file_id, name: file.name }] }));
      toast.success('File attached');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to attach file');
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  if (loading) return <div className="flex justify-center py-12" data-testid="my-conduct-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="my-conduct-tab">
      {restrictions.length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="my-restrictions-card">
          <CardHeader><CardTitle className="text-amber-800 text-base">Active Restrictions</CardTitle></CardHeader>
          <CardContent className="text-sm text-amber-700">{restrictions.map(restrictionLabel).join(', ')}</CardContent>
        </Card>
      )}

      <div>
        <h3 className="font-semibold mb-2">My Cases</h3>
        {cases.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-cases-msg">No conduct cases involving you.</p>}
        <div className="space-y-3">
          {cases.map((c) => (
            <Card key={c.id} data-testid={`my-case-${c.id}`}>
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{categoryName(c.category)}</span>
                  <Badge variant="outline">{statusLabel(c.status, c, 'employee')}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{c.what_happened}</p>
                {isNewChannelImpactCase(c) ? (
                  <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={`case-channel-impact-${c.id}`}>
                    <Badge variant="outline">Channel: {channelDisplayLabel(c)}</Badge>
                    {impactDisplayTags(c).map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
                    {c.impact_usd != null && <Badge variant="outline">${Number(c.impact_usd).toLocaleString()}</Badge>}
                    {c.impact_text && <span className="text-muted-foreground">{c.impact_text}</span>}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid={`case-legacy-impact-${c.id}`}>
                    Channel: {c.channel || '—'}{c.what_it_caused ? ` · ${c.what_it_caused}` : ''}{c.impact_text ? ` · ${c.impact_text}` : ''}
                  </p>
                )}
                {c.status === 'AWAITING_STATEMENT' && (
                  <div className="space-y-2 pt-2">
                    <p className="text-xs text-amber-700" data-testid={`statement-due-${c.id}`}>
                      Your statement is due by {fmtDate(c.deadlines?.statement_due_at)}
                    </p>
                    <Textarea
                      data-testid={`statement-input-${c.id}`}
                      placeholder="Your statement in reply to this report..."
                      value={drafts[c.id] || ''}
                      onChange={(e) => setDrafts({ ...drafts, [c.id]: e.target.value })}
                    />
                    <div className="flex items-center gap-2">
                      <input type="file" data-testid={`statement-file-input-${c.id}`} className="text-xs"
                        disabled={uploading[`case_${c.id}`]}
                        onChange={(e) => { if (e.target.files[0]) uploadFile('case', c.id, e.target.files[0]); e.target.value = ''; }} />
                      {(attached[`case_${c.id}`] || []).map((f) => (
                        <span key={f.id} className="text-xs text-muted-foreground" data-testid={`statement-attached-${f.id}`}>{f.name}</span>
                      ))}
                    </div>
                    <Button size="sm" data-testid={`submit-statement-btn-${c.id}`} onClick={() => submitStatement(c.id)}>Submit Statement</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-2">My Cards</h3>
        {cards.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-cards-msg">No conduct cards on your record.</p>}
        <div className="space-y-3">
          {cards.map((card) => (
            <Card key={card.id} data-testid={`my-card-${card.id}`}>
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <Badge className={levelBadgeClass(card.level)} data-testid={`card-level-${card.id}`}>{levelLabel(card.level)}</Badge>
                  <Badge variant="outline">{statusLabel(card.status, card, 'employee')}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">Issued {fmtDate(card.issued_at)}</p>
                {card.restriction_codes?.length > 0 && (
                  <p className="text-xs text-amber-700">Restrictions: {card.restriction_codes.map(restrictionLabel).join(', ')}</p>
                )}
                {(card.letter_file_id || card.show_cause?.file_id) && (
                  <Button size="sm" variant="ghost" className="px-0" data-testid={`download-letter-btn-${card.id}`}
                    onClick={() => downloadFile(card.letter_file_id || card.show_cause.file_id, `conduct_${card.id}.pdf`)}>
                    <Download className="h-3.5 w-3.5 mr-1" />Download Letter
                  </Button>
                )}
                {noticeLine(card) && (
                  <p className="text-xs text-amber-700" data-testid={`notice-period-${card.id}`}>{noticeLine(card)}</p>
                )}
                {card.cap && (card.cap.actions || []).length > 0 && (
                  <div className="rounded-md border bg-muted/30 p-3 space-y-1" data-testid={`my-cap-${card.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Corrective Action Plan</p>
                      <span className="text-xs text-muted-foreground" data-testid={`my-cap-status-${card.id}`}>
                        {card.cap.accepted_at ? `Accepted ${fmtDate(card.cap.accepted_at)}` : card.cap.approved_by ? 'Awaiting your acceptance' : 'Awaiting HR approval'}
                        {' · '}Reviews {(card.cap.reviews || []).length}/{(card.cap.review_days || []).length}
                        {(card.cap.review_days || []).length > 0 && ` (days ${card.cap.review_days.join(', ')})`}
                      </span>
                    </div>
                    <ul className="text-xs space-y-0.5">
                      {card.cap.actions.map((a, i) => (
                        <li key={a.id || i} data-testid={`my-cap-action-${card.id}-${i}`}>
                          {a.text}{a.target ? ` — target: ${a.target}` : ''}{a.check_method ? ` (check: ${a.check_method})` : ''} · due day {a.due_review_day} · {ACTION_STATUS_LABELS[a.status] || a.status}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-1 items-center">
                  {!card.ack_at && ['ACTIVE_CAP', 'CLEAN_PERIOD', 'SHOW_CAUSE'].includes(card.status) && (
                    <Button size="sm" variant="outline" data-testid={`ack-btn-${card.id}`} onClick={() => acknowledge(card.id)}>Acknowledge</Button>
                  )}
                  {card.cap && card.cap.approved_by && !card.cap.accepted_at && (
                    <Button size="sm" variant="outline" data-testid={`accept-cap-btn-${card.id}`} onClick={() => acceptCap(card.cap.id)}>Accept CAP</Button>
                  )}
                  {card.can_appeal && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <Textarea
                        className="w-56 h-8 text-xs"
                        data-testid={`appeal-input-${card.id}`}
                        placeholder="Grounds for appeal"
                        value={drafts[`appeal_${card.id}`] || ''}
                        onChange={(e) => setDrafts({ ...drafts, [`appeal_${card.id}`]: e.target.value })}
                      />
                      <input type="file" data-testid={`appeal-file-input-${card.id}`} className="text-xs"
                        disabled={uploading[`card_${card.id}`]}
                        onChange={(e) => { if (e.target.files[0]) uploadFile('card', card.id, e.target.files[0]); e.target.value = ''; }} />
                      <Button size="sm" variant="outline" data-testid={`appeal-btn-${card.id}`} onClick={() => fileAppeal(card.id)}>Appeal</Button>
                      {(attached[`card_${card.id}`] || []).map((f) => (
                        <span key={f.id} className="text-xs text-muted-foreground" data-testid={`appeal-attached-${f.id}`}>{f.name}</span>
                      ))}
                    </div>
                  )}
                  {card.has_appeal && <span className="text-xs text-muted-foreground" data-testid={`appeal-filed-label-${card.id}`}>Appeal filed</span>}
                  {card.level === 'RED' && card.status === 'SHOW_CAUSE' && !card.show_cause?.reply && (
                    <div className="flex gap-2 items-center w-full">
                      <Textarea
                        className="w-72 text-xs"
                        data-testid={`show-cause-reply-input-${card.id}`}
                        placeholder="Your reply to the show-cause notice"
                        value={drafts[`sc_${card.id}`] || ''}
                        onChange={(e) => setDrafts({ ...drafts, [`sc_${card.id}`]: e.target.value })}
                      />
                      <Button size="sm" data-testid={`show-cause-reply-btn-${card.id}`} onClick={() => showCauseReply(card.id)}>Send Reply</Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
